import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MoreHorizontal, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import { MODULES, ROUTE_MODULE_MAP, type AppModule } from './modules.config';
import api from '@/lib/axios';

const ALWAYS_ON_MODULES = new Set(['dashboard', 'configuracion', 'vademecum', 'tareas']);
const MAX_TABS = 5;

function getVisibleSections(module: AppModule, rolId?: string) {
  return module.sections.filter(s => !rolId || s.roles.includes(rolId));
}

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario } = useAuthStore();
  const [moreOpen, setMoreOpen] = useState(false);

  const { data: configData } = useQuery({
    queryKey: ['configuracion'],
    queryFn: () => api.get('/api/configuracion').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const modulosHabilitados: Record<string, boolean> = configData?.modulosHabilitadosJson ?? {};

  const visibleModules = MODULES.filter(m => {
    if (!m.sections.some(s => !usuario?.rolId || s.roles.includes(usuario.rolId))) return false;
    if (ALWAYS_ON_MODULES.has(m.id)) return true;
    if (!configData) return true;
    const hasSomeEnabled = Object.keys(modulosHabilitados).length > 0;
    return hasSomeEnabled ? modulosHabilitados[m.id] !== false : true;
  });

  const basePath = '/' + location.pathname.split('/')[1];
  const activeModuleId = ROUTE_MODULE_MAP[basePath] || '';

  const tabModules = visibleModules.slice(0, MAX_TABS);
  const moreModules = visibleModules.slice(MAX_TABS);
  const moreIsActive = moreModules.some(m => m.id === activeModuleId);

  function getFirstPath(module: AppModule): string {
    const sections = getVisibleSections(module, usuario?.rolId);
    return sections[0]?.to ?? '/dashboard';
  }

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-100"
        style={{
          boxShadow: '0 -1px 8px rgba(0,0,0,0.06)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="flex items-stretch h-16">
          {tabModules.map(module => {
            const Icon = module.icon;
            const isActive = activeModuleId === module.id;
            return (
              <button
                key={module.id}
                onClick={() => navigate(getFirstPath(module))}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 transition-colors"
                style={{ color: isActive ? module.color : '#94a3b8' }}
              >
                <div
                  className="rounded-xl px-3 py-1 transition-colors"
                  style={isActive ? { backgroundColor: `${module.color}20` } : undefined}
                >
                  <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                </div>
                <span className="text-[10px] font-medium leading-none truncate max-w-[60px] text-center">
                  {module.label}
                </span>
              </button>
            );
          })}

          {moreModules.length > 0 && (
            <button
              onClick={() => setMoreOpen(true)}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 transition-colors"
              style={{ color: moreIsActive ? '#0e85bf' : '#94a3b8' }}
            >
              <div
                className="rounded-xl px-3 py-1 transition-colors"
                style={moreIsActive ? { backgroundColor: '#0e85bf20' } : undefined}
              >
                <MoreHorizontal size={20} strokeWidth={1.8} />
              </div>
              <span className="text-[10px] font-medium leading-none">Más</span>
            </button>
          )}
        </div>
      </nav>

      {/* Bottom sheet — módulos adicionales */}
      <Dialog.Root open={moreOpen} onOpenChange={setMoreOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="md:hidden fixed inset-0 bg-black/40 z-50" />
          <Dialog.Content
            className="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl focus:outline-none flex flex-col"
            style={{ maxHeight: '82vh', paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {/* Handle visual */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>

            <div className="flex items-center justify-between px-5 py-3 flex-shrink-0 border-b border-gray-100">
              <Dialog.Title className="text-base font-bold text-text-primary">
                Más módulos
              </Dialog.Title>
              <Dialog.Close className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
                <X size={18} />
              </Dialog.Close>
            </div>

            <div className="flex-1 overflow-y-auto pb-6 px-3 min-h-0">
              {moreModules.map(module => {
                const Icon = module.icon;
                const isActive = activeModuleId === module.id;
                const sections = getVisibleSections(module, usuario?.rolId);

                return (
                  <div key={module.id}>
                    {module.dividerBefore && (
                      <div className="my-2 h-px bg-gray-100" />
                    )}

                    {sections.length === 1 ? (
                      <button
                        onClick={() => { navigate(sections[0].to); setMoreOpen(false); }}
                        className={cn(
                          'w-full flex items-center gap-3.5 px-3 py-3 rounded-xl transition-colors text-left',
                          isActive ? 'bg-gray-50' : 'hover:bg-gray-50'
                        )}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${module.color}18` }}
                        >
                          <Icon size={20} style={{ color: module.color }} />
                        </div>
                        <span className={cn(
                          'flex-1 text-sm font-semibold',
                          isActive ? 'text-text-primary' : 'text-text-secondary'
                        )}>
                          {module.label}
                        </span>
                        {isActive && (
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: module.color }}
                          />
                        )}
                      </button>
                    ) : (
                      <div>
                        <div className="flex items-center gap-3.5 px-3 py-2.5">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${module.color}18` }}
                          >
                            <Icon size={20} style={{ color: module.color }} />
                          </div>
                          <span className="text-sm font-semibold text-text-primary">
                            {module.label}
                          </span>
                        </div>
                        <div className="ml-[3.25rem] space-y-0.5 pb-1">
                          {sections.map(sec => {
                            const secActive =
                              location.pathname === sec.to ||
                              (!sec.end && location.pathname.startsWith(sec.to + '/'));
                            return (
                              <button
                                key={sec.to}
                                onClick={() => { navigate(sec.to); setMoreOpen(false); }}
                                className={cn(
                                  'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-left transition-colors',
                                  secActive
                                    ? 'bg-gray-100 font-semibold text-text-primary'
                                    : 'text-text-secondary hover:bg-gray-50'
                                )}
                              >
                                <sec.icon size={14} strokeWidth={secActive ? 2.5 : 2} className="flex-shrink-0" />
                                {sec.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
